// Canonical funnel step sequences. Every step page derives its StepIndicator from these,
// so keep them in sync with the actual page-to-page routing.
//
// The array entries are i18n KEYS, not display copy — StepIndicator resolves each one against
// common.stepIndicator.<key>, so every locale shows the step name in its own language.
//
// NOTE: two steps are TEMPORARILY HIDDEN and therefore absent from these arrays:
//  - "style": upload defaults car jobs to vintage-poster and skips /style (see upload/page.tsx).
//  - "look": the realism step; /theme routes straight to /mood (see theme/page.tsx).
// Re-add the key here (and restore the routing) to re-enable either step.

/** Car flow: vehicle → upload → theme → mood → light → text → people → orientation → rendering → preview → checkout. */
export const VINTAGE_STEPS: readonly string[] = [
  "vehicle",
  "upload",
  "theme",
  "mood",
  "light",
  "text",
  "people",
  "orientation",
  "rendering",
  "preview",
  "checkout",
];

/**
 * Motorcycle flow: vehicle → upload → rider → theme → mood → light → text → orientation → rendering
 * → preview → checkout.
 *
 * The rider step sits third, where the car flow puts people seventh. On a car poster people are
 * optional cargo inside the cabin; on a bike poster the rider IS the subject, and asking early is
 * what makes the rest of the funnel — the shot, the helmet, the framing — mean anything. There is
 * no separate people step afterwards: the rider step already collects both photos.
 */
export const MOTO_STEPS: readonly string[] = [
  "vehicle",
  "upload",
  "rider",
  "theme",
  "mood",
  "light",
  "text",
  "orientation",
  "rendering",
  "preview",
  "checkout",
];

/**
 * Pets flow: pets → style → place → pose → text → orientation → rendering → preview → checkout.
 *
 * There is no "vehicle" step: the pets lander skips that question, and the pets tile on it leaves
 * the car and motorcycle flow. "place" is the walk for the Vintage Poster and the dress for the Old
 * Master; the other three styles skip it, and the indicator keeps it so the count does not jump.
 */
export const PET_STEPS: readonly string[] = [
  "pets",
  "style",
  "place",
  "pose",
  "text",
  "orientation",
  "rendering",
  "preview",
  "checkout",
];

/**
 * Family Christmas card: who → scene → greeting → rendering → preview → checkout. No orientation
 * step: the job is created landscape and the greeting step starts the render.
 */
export const FAMILY_CARD_STEPS: readonly string[] = [
  "upload",
  "place",
  "text",
  "rendering",
  "preview",
  "checkout",
];

/** The family card pack's slug prefix (backend FamilyCardPacks). */
export const FAMILY_CARD_STYLE_PREFIX = "card-family-";
export const FAMILY_CARD_STYLE = "card-family-vintage";

export function isFamilyCardStyle(styleSlug: string | null | undefined): boolean {
  return !!styleSlug && styleSlug.startsWith(FAMILY_CARD_STYLE_PREFIX);
}

/** Short flow for the fixed-template styles (preselected via /upload?style=...): includes people customization. */
export const CLASSIC_STEPS: readonly string[] = [
  "vehicle",
  "upload",
  "people",
  "orientation",
  "rendering",
  "preview",
  "checkout",
];

/** 1-based position of a step key within a flow, for StepIndicator's currentStep. */
export function stepNumber(steps: readonly string[], key: string): number {
  const idx = steps.indexOf(key);
  return idx === -1 ? 1 : idx + 1;
}

export function stepsForStyle(styleSlug: string | null | undefined): readonly string[] {
  if (styleSlug === MOTO_STYLE_SLUG) return MOTO_STEPS;
  if (isPetStyle(styleSlug)) return PET_STEPS;
  if (isFamilyCardStyle(styleSlug)) return FAMILY_CARD_STEPS;
  return styleSlug && styleSlug !== "vintage-poster" ? CLASSIC_STEPS : VINTAGE_STEPS;
}

/** The pack a motorcycle job runs on. Its presence is what marks the job as a motorcycle. */
export const MOTO_STYLE_SLUG = "moto-poster";

export function isMotoStyle(styleSlug: string | null | undefined): boolean {
  return styleSlug === MOTO_STYLE_SLUG;
}

/** Every pets-line pack starts with this prefix; its presence is what marks the job as a pet job. */
export const PET_STYLE_PREFIX = "pet-";

/** The pack a new pet job is created on — the style step preselects it. */
export const PET_DEFAULT_STYLE = "pet-vintage-poster";

export function isPetStyle(styleSlug: string | null | undefined): boolean {
  return !!styleSlug && styleSlug.startsWith(PET_STYLE_PREFIX);
}

const STORAGE_KEY_PREFIX = "vellum:funnel-style:";

/** Persist the job's style so later (style-agnostic) pages can show the right flow. */
export function rememberFunnelStyle(jobId: string, styleSlug: string): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY_PREFIX + jobId, styleSlug);
  } catch {
    // Storage unavailable (private mode, quota) — later pages fall back to the vintage flow.
  }
}

export function readFunnelStyle(jobId: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem(STORAGE_KEY_PREFIX + jobId);
  } catch {
    return null;
  }
}
